import { apiClient, AUTH_REQUEST_TIMEOUT_MS } from "@/services/apiClient";
import {
  clearAuth,
  getToken,
  setSessionExpiresAt,
  setStoredUser,
  setToken,
} from "@/services/authStorage";

export interface RequestLoginLinkParams {
  email: string;
  locale?: string;
}

export function requestLoginLink(params: RequestLoginLinkParams): Promise<void> {
  return apiClient
    .post("/auth/login-link", {
      email: params.email.trim().toLowerCase(),
      locale: params.locale,
    })
    .then(() => undefined);
}

export interface VerifyResponse {
  accessToken: string;
  sessionId: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profileImage?: string | null;
    tenantId: string | null;
    tenantName: string | null;
    language: string;
    industry: string | null;
    profession: string | null;
    emailVerifiedAt?: string | null;
    platformRole?: "root" | "root_admin" | null;
  };
}

export function loginWithPassword(params: {
  email: string;
  password: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/login", {
      email: params.email.trim().toLowerCase(),
      password: params.password,
    })
    .then((res) => res.data);
}

export type CompanyTypeValue =
  | "unipersonal"
  | "sas"
  | "srl"
  | "sa"
  | "monotributo";

export interface ValidateRutResponse {
  legalName: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
  suggestedCompanyType?: CompanyTypeValue | null;
}

export function validateRut(rut: string): Promise<ValidateRutResponse> {
  const normalized = rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
  return apiClient
    .post<ValidateRutResponse>("/auth/validate-rut", { rut: normalized })
    .then((res) => res.data);
}

export function sendVerificationEmail(
  email: string,
  locale?: string,
  captchaToken?: string
): Promise<void> {
  return apiClient
    .post("/auth/send-verification-email", {
      email: email.trim().toLowerCase(),
      ...(locale != null && locale !== "" && { locale }),
      ...(captchaToken != null && captchaToken !== "" && { captchaToken }),
    })
    .then(() => undefined);
}

export function verifyEmail(
  email: string,
  code: string
): Promise<{ emailVerified: true; registrationToken: string }> {
  return apiClient
    .post<{ emailVerified: true; registrationToken: string }>(
      "/auth/verify-email",
      { email: email.trim().toLowerCase(), code: code.trim() }
    )
    .then((res) => res.data);
}

export interface RegisterParams {
  email: string;
  firstName?: string;
  lastName?: string;
  password: string;
  rut?: string;
  razonSocial?: string;
  registrationToken: string;
  accountType?: "business" | "accountant";
  partnerCode?: string;
}

export function register(params: RegisterParams): Promise<VerifyResponse> {
  const body: Record<string, unknown> = {
    email: params.email.trim().toLowerCase(),
    ...(params.firstName?.trim() ? { firstName: params.firstName.trim() } : {}),
    ...(params.lastName?.trim() ? { lastName: params.lastName.trim() } : {}),
    password: params.password,
    registrationToken: params.registrationToken,
  };
  if (params.rut != null && params.rut.trim() !== "") {
    const normalizedRut = params.rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
    body.rut = normalizedRut;
    body.razonSocial = params.razonSocial?.trim() ?? "";
  }
  if (params.accountType) {
    body.accountType = params.accountType;
  }
  if (params.partnerCode?.trim()) {
    body.partnerCode = params.partnerCode.trim();
  }
  return apiClient.post<VerifyResponse>("/auth/register", body).then((res) => res.data);
}

export interface AccountantProfileResponse {
  accountType: "business" | "accountant";
  profile: {
    kind: "solo" | "studio";
    studioName: string | null;
    studioLogoUrl: string | null;
    studioAddress: string | null;
    studioPhone: string | null;
  } | null;
}

export function getMyAccountantProfile(): Promise<AccountantProfileResponse> {
  return apiClient
    .get<AccountantProfileResponse>("/auth/me/accountant-profile")
    .then((res) => res.data);
}

export interface SaveAccountantProfileParams {
  kind: "solo" | "studio";
  studioName?: string;
  studioLogoUrl?: string;
  studioAddress?: string;
  studioPhone?: string;
}

export function saveMyAccountantProfile(
  params: SaveAccountantProfileParams,
): Promise<void> {
  return apiClient
    .post("/auth/me/accountant-profile", params)
    .then(() => undefined);
}

export function uploadAccountantStudioLogo(
  file: File,
): Promise<{ studioLogoUrl: string }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiClient
    .post<{ studioLogoUrl: string }>(
      "/auth/me/accountant-profile/logo",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export interface CreateCompanyParams {
  rut: string;
  legalName?: string;
  companyType?: CompanyTypeValue;
  address?: string;
  city?: string;
  department?: string;
  phone?: string;
  contactEmail?: string;
  industry?: string;
  profession?: string;
  partnerCode?: string;
}

export function createCompany(params: CreateCompanyParams): Promise<VerifyResponse> {
  const normalizedRut = params.rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
  const body: Record<string, unknown> = {
    rut: normalizedRut,
  };
  if (params.legalName != null && params.legalName.trim() !== "") body.legalName = params.legalName.trim();
  if (params.companyType != null) body.companyType = params.companyType;
  if (params.address != null && params.address.trim() !== "") body.address = params.address.trim();
  if (params.city != null && params.city.trim() !== "") body.city = params.city.trim();
  if (params.department != null && params.department.trim() !== "") body.department = params.department.trim();
  if (params.phone != null && params.phone.trim() !== "") body.phone = params.phone.trim();
  if (params.contactEmail != null && params.contactEmail.trim() !== "") body.contactEmail = params.contactEmail.trim();
  if (params.industry != null && params.industry.trim() !== "") body.industry = params.industry.trim();
  if (params.profession != null && params.profession.trim() !== "") body.profession = params.profession.trim();
  if (params.partnerCode != null && params.partnerCode.trim() !== "") body.partnerCode = params.partnerCode.trim();
  return apiClient.post<VerifyResponse>("/auth/create-company", body).then((res) => res.data);
}

export interface SetPasswordParams {
  currentPassword?: string;
  newPassword: string;
}

export function setPassword(params: SetPasswordParams): Promise<void> {
  return apiClient
    .patch("/auth/me/password", {
      currentPassword: params.currentPassword,
      newPassword: params.newPassword,
    })
    .then(() => undefined);
}

export function verify(params: {
  token: string;
  activeTenantId?: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/verify", {
      token: params.token,
      activeTenantId: params.activeTenantId,
    })
    .then((res) => res.data);
}

export function verifyRootInvite(token: string): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/verify-root-invite", { token })
    .then((res) => res.data);
}

export function exchangeSsoCode(code: string): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/sso/exchange", { code })
    .then((res) => res.data);
}

export function requestPasswordReset(email: string): Promise<void> {
  return apiClient
    .post("/auth/forgot-password", { email: email.trim().toLowerCase() })
    .then(() => undefined);
}

export function resetPasswordWithToken(
  token: string,
  newPassword: string
): Promise<void> {
  return apiClient
    .post("/auth/reset-password", { token, newPassword })
    .then(() => undefined);
}

export interface SessionResponse {
  userId: string;
  activeTenantId: string | null;
  role: string;
  sessionId: string;
  tenantName: string | null;
  tenantProfileImage: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
  platformRole: "root" | "root_admin" | null;
  onboardingCompletedAt: string | null;
  onboardingMode: "self" | "accountant" | null;
  planId: string | null;
  hasDgiCredentials: boolean;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  identityVerifiedAt: string | null;
  profileImage: string | null;
  emailVerifiedAt: string | null;
  accountType: "business" | "accountant";
}

export function getSession(): Promise<SessionResponse> {
  return apiClient
    .get<SessionResponse>("/auth/session", {
      // This call gates AuthContext.isLoading, which gates ProtectedRoute. If
      // it never settles, every route in the portal is a full-screen spinner
      // for as long as the tab stays open. Failing is strictly better.
      timeout: AUTH_REQUEST_TIMEOUT_MS,
    })
    .then((res) => res.data);
}

export interface TenantOption {
  id: string;
  name: string | null;
  role: string;
}

export function getMyTenants(): Promise<TenantOption[]> {
  return apiClient
    .get<{ tenants: TenantOption[] }>("/auth/me/tenants")
    .then((res) => res.data.tenants);
}

export type AccountantTenantSeverity = "urgent" | "attention" | "ok";
export type AccountantTenantFlag =
  | "onboarding"
  | "boletos"
  | "sin-dgi"
  | "activo"
  | "iva"
  | "vencimiento"
  /** Received CFEs awaiting an accept/reject decision. */
  | "ack"
  /** No current-year Decreto 206/019 declaration on file. */
  | "beneficio-fiscal"
  /** Billable subscription is past_due or lapsed (a payment problem). */
  | "pago-pendiente";

export interface TenantSummary {
  id: string;
  name: string | null;
  rut: string | null;
  role: string;
  profileImage: string | null;
  /** Brand colour derived from the tenant's logo; null when never set. */
  brandColor: string | null;
  /** Fiscal regime, from the tenant's fiscal profile — see
   *  describeTenantRegime. Both null/false means the profile has not been
   *  derived yet, which is NOT a regime. */
  ivaRegime: string | null;
  isMonotributo: boolean;
  companyType: string | null;
  invoicesThisMonth: number;
  rejectedInvoices: number;
  pendingBoletosThisMonth: number;
  hasDgiSetup: boolean;
  onboardingPending: boolean;
  /**
   * Strictly whether the owner completed the onboarding wizard. Unlike
   * onboardingPending, this stays true for a finished-but-still-test tenant.
   * Drives routing (only genuinely-incomplete tenants open the wizard) and
   * hard-delete trash visibility (completed tenants use the baja flow).
   */
  onboardingCompleted: boolean;
  /**
   * ISO timestamp when the tenant was dado de baja (offboarded), or null.
   * Baja'd tenants are excluded from the default cartera + counts and shown
   * only under the "Dadas de baja" filter (still reactivable from there).
   */
  bajaAt: string | null;
  /** Received CFEs still awaiting an accept/reject decision (DGI: 20 days). */
  pendingAckCount: number;
  lastActivityAt: string | null;
  /**
   * IVA balance UYU for the current period (débito − crédito). Negative
   * means saldo a favor del contribuyente (render olive); positive means
   * debt to pay (render warn). USD invoices contribute via the
   * per-invoice exchange rate locked at issuance; EUR is excluded.
   */
  ivaPeriodTotalUyu: number;
  severity: AccountantTenantSeverity;
  flags: AccountantTenantFlag[];
  /** 0..100 — simple deductive score, used for the pulse average + sort. */
  healthScore: number;
  /** Effective plan id ('free' | 'pro' | 'ultra'). Drives the plan badge
   *  and the "Migrar a plan pago" 3-dots action on free tenants. */
  planId: string;
  /** Billing cadence — 'monthly' | 'annual' | null when free / unset. */
  billingInterval: string | null;
  /** Subscription health — 'past_due'/'lapsed' drive the payment-issue chip. */
  subscriptionState: "active" | "past_due" | "lapsed" | "none";
}

export interface AccountantPulse {
  totalClients: number;
  activeClients: number;
  onboardingClients: number;
  attentionToday: number;
  cfeThisMonth: number;
  cfeMonthGrowthPct: number;
  /** Average across active (non-onboarding) tenants only. Null = no active clients yet. */
  avgHealthScore: number | null;
  topClientsAt100: { name: string | null; count: number };
  filterCounts: {
    all: number;
    attention: number;
    onboarding: number;
    boletos: number;
    "sin-dgi": number;
    activo: number;
    iva: number;
    vencimiento: number;
    "pago-pendiente": number;
    baja: number;
  };
}

export function getMyAccountantPulse(): Promise<AccountantPulse> {
  return apiClient
    .get<AccountantPulse>("/auth/me/accountant/pulse")
    .then((res) => res.data);
}

/**
 * Hard-delete an onboarding-incomplete tenant. Backend rejects if the
 * tenant has finished onboarding — those go through the existing
 * support-reviewed delete request.
 */
export function deleteOnboardingTenant(tenantId: string): Promise<void> {
  return apiClient.delete(`/tenants/${tenantId}`).then(() => undefined);
}

export function getMyTenantsSummary(): Promise<TenantSummary[]> {
  return apiClient
    .get<{ tenants: TenantSummary[] }>("/auth/me/tenants/summary")
    .then((res) => res.data.tenants);
}

export interface MyAccountantRequest {
  id: string;
  tenantId: string;
  tenantName: string | null;
  requestType: string;
  status: "pending" | "in_progress" | "completed";
  createdAt: string;
  fiscalObligation: {
    id: string;
    taxType: string;
    period: string;
    dueDate: string;
    status: string;
  } | null;
}

export function getMyAccountantRequests(
  statuses?: Array<"pending" | "in_progress" | "completed">,
): Promise<MyAccountantRequest[]> {
  const params =
    statuses && statuses.length > 0
      ? { statuses: statuses.join(",") }
      : undefined;
  return apiClient
    .get<{ requests: MyAccountantRequest[] }>("/auth/me/requests", { params })
    .then((res) => res.data.requests);
}

export function selectTenant(tenantId: string): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/select-tenant", { tenantId })
    .then((res) => res.data);
}

export function logout(params?: { sessionId?: string }): Promise<void> {
  return apiClient
    .post("/auth/logout", params?.sessionId != null ? { sessionId: params.sessionId } : {})
    .then(() => undefined)
    .finally(() => {
      clearAuth();
    });
}

export interface TenantCurrentResponse {
  id: string;
  name: string;
  legalName: string | null;
  rut: string;
  address: string | null;
  city: string | null;
  department: string | null;
  phone: string | null;
  contactEmail: string | null;
  activityStartDate: string | null;
  literalE: boolean | null;
  literalEVerified: boolean | null;
  language: string;
  industry: string | null;
  profession: string | null;
  companyType: CompanyTypeValue | null;
  profileImage: string | null;
  invoiceLogoImage: string | null;
  /** Crop applied to invoiceLogoImage on the invoice PDF, as fractions of the
   *  source image's natural dimensions (0..1). Defaults 0/0/1/1 = whole image. */
  invoiceLogoCropX: number;
  invoiceLogoCropY: number;
  invoiceLogoCropW: number;
  invoiceLogoCropH: number;
  /** Point size of the box the cropped logo is fitted into on the PDF. */
  invoiceLogoSizePt: number;
  brandColor: string | null;
  createdAt: string;
  onboardingCompletedAt: string | null;
  /** Homologación-simplificada status for this tenant (null when no request
   *  exists). Self-scratch onboarding parks on the "homologación pendiente"
   *  screen until this is completed/vigente, then resumes cert → CAE. */
  homologacionStatus: string | null;
  /** Self-onboarded nuevo emisor whose producción user is not yet confirmed:
   *  the wizard must park them on that step. Server-computed, and the same
   *  condition as the `produccion_role_unconfirmed` promotion gate. */
  produccionRoleRequired?: boolean;
  fiscalProfileSkippedAt: string | null;
  /** Set when user explicitly skipped the signing-certificate upload step —
   *  the .p12 from Abitab / Correo Uruguayo / Antel, NOT anything DGI issues.
   *  The column is named dgiCertificateSkippedAt for historical reasons. Lets
   *  onboarding advance without a cert (the user has to upload it later in
   *  Settings before they can actually issue invoices). */
  dgiCertificateSkippedAt: string | null;
  /** Set when user acknowledged step 6 "go to Abitab" (Fiscalo path). Enables awaiting-Abitab screen. */
  step6AbitabAcknowledgedAt: string | null;
  /** Self-service onboarding: user completed or skipped the CFE import step (after CAE). */
  onboardingCfeImportStepCompletedAt: string | null;
  /** Onboarding routing state — durable, so it survives a tab close or a new device. */
  onboardingCertChoice: "myself" | "fiscalo" | null;
  onboardingCertPath: "manual" | "fiscalo" | null;
  onboardingCertAcquisitionDoneAt: string | null;
  onboardingCertWaitContinuedAt: string | null;
  onboardingPersonalizeDoneAt: string | null;
  hasDgiCredentials: boolean;
  planId: string | null;
  billingInterval: string | null;
  /**
   * Resolved current-plan display metadata. Handles legacy/grandfathered plan
   * ids (pro/free/ultra/…) that aren't in the selectable tiers, so the current
   * plan is labeled correctly (legacy ids map to their new-tier name).
   */
  plan: {
    id: string;
    nameKey: string;
    /** Catalog list price, sin IVA. NOT necessarily what this tenant pays. */
    priceMonthly: number;
    /** Catalog list price, sin IVA. NOT necessarily what this tenant pays. */
    priceAnnual: number;
    maxCfesPerMonth: number | null;
    /** What this tenant is charged monthly, sin IVA — 80 UI credit deducted. */
    tenantPriceMonthly: number;
    /** Null when annual is not offered (benefit tenants are monthly-only). */
    tenantPriceAnnual: number | null;
    /** Monthly 80 UI discount applied, sin IVA. 0 when no benefit applies. */
    fiscalBenefitDiscount: number;
  } | null;
  /**
   * New-taxonomy plan ids this tenant may self-select (derived from the fiscal
   * profile). The plan picker shows a plan iff it's here OR it's the current plan.
   */
  eligiblePlanIds: string[];
  /**
   * Régimen especial from the fiscal profile (currently only
   * 'automotoras_1222_008'). Gates the extra IVA rates the invoice line
   * offers; null for almost every tenant.
   */
  specialRegime: string | null;
  /**
   * IVA regime from the fiscal profile. 'minimum' = Literal E, the one regime
   * that genuinely invoices at 0%; the invoice line locks its rate on this
   * rather than on the exoneration leyenda flag.
   */
  ivaRegime: string | null;
  /**
   * True once a fiscal-benefit declaration (Decreto 206/019) exists for the
   * current year — comply OR not. The onboarding régimen gate reads it as the
   * durable "declaration answered" signal so a refresh doesn't re-show it.
   */
  fiscalBenefitDeclared: boolean;
  planSubscriptionPeriodStart: string | null;
  planSubscriptionPeriodEnd: string | null;
  /**
   * Billing health for the reactivate CTA: 'none' (free/unbillable), 'active',
   * 'past_due' (period ended within grace, or the latest charge was declined),
   * or 'lapsed' (past the grace window).
   */
  subscriptionState: "active" | "past_due" | "lapsed" | "none";
  lastPlanCheckoutId: string | null;
  planResolvedAt: string | null;
  deletionRequestedAt: string | null;
  exoneratedRegimeEnabled: boolean | null;
  exoneratedRegimeLegend: string | null;
  exoneratedRegimeHeaderLegend: string | null;
  /** The tenant's standing adenda (CFE Zona J); null = none set. */
  adendaText: string | null;
  /** When true (default), Fiscalo auto-accepts received CFEs on day 19
   *  of DGI's 20-día decision window (§XVIII). */
  autoAcceptReceivedCfe: boolean;
  migratingFromAnotherPlatform: boolean | null;
  onboardingSituationChoice:
    | "scratch"
    | "issuer_need_regularization"
    | "already_issuer"
    | null;
  onboardingMode: "self" | "accountant" | null;
  hasDgiPortalUser: boolean;
  dgiPortalUserConfirmedAt: string | null;
  dgiPortalUserConfirmed: boolean;
  dgiPortalUserAccountantClaimedAt: string | null;
  dgiPortalUserAccountantClaimed: boolean;
  /**
   * Per-step completion timestamps for the DGI auto-onboarding chain
   * (portal_user / company_info / cae_download /
   * proveedor_software). Also carries `started_at`, `_failures` (per-step
   * retry counter), and `_failed` (terminal-failure marker → string error
   * message). The dashboard onboarding card reads `_failed` to surface
   * the manual Reintentar CTA.
   */
  dgiAutoOnboardingProgress: Record<string, unknown> | null;
  /** Derived self-service provider-habilitación state (drives the Home
   *  "Activar Fiscalo para emitir" card). */
  fiscaloConfig: {
    idemisorSync: boolean;
    proveedorSoftware: boolean;
    failed: string | null;
    prerequisitesMet: boolean;
  };
  /** Real emission allowed — Fiscalo registered as software provider in DGI. */
  emisionEnabled: boolean;
  /** Has issued ≥1 CFE — grandfathers existing emitters past the emission gate. */
  hasEmittedCfe: boolean;
  /** Resolved DGI environment (test = ePrueba, prod = live). */
  dgiEnvironment: "test" | "prod";
  /** Strict DGI air-gap for demo / showcase tenants. When true, the
   *  dashboard hides DGI-only nudges (certificate upload, etc.) since
   *  invoices land confirmed without ever touching DGI. */
  bypassDgi: boolean;
  /** DGI sucursal code echoed in CFE Emisor (CdgDGISucur). Defaults to 1. */
  cdgDGISucur: number;
}

export interface TenantPlanOption {
  id: string;
  nameKey: string;
  descriptionKey: string;
  /** Catalog list price, sin IVA. NOT necessarily what this tenant pays. */
  priceMonthly: number;
  /** Catalog list price, sin IVA. NOT necessarily what this tenant pays. */
  priceAnnual: number;
  maxCfesPerMonth: number | null;
  /**
   * What THIS tenant is actually charged monthly, sin IVA — the 80 UI (Dec.
   * 206/019) credit already deducted for Art. 5 (Literal E / IVA mínimo) tenants
   * holding a current declaration. Equals `priceMonthly` for everyone else.
   *
   * Always quote this. `priceMonthly` is kept alongside so a discounted card can
   * show the struck-through list price next to it.
   */
  tenantPriceMonthly: number;
  /**
   * What this tenant pays annually, sin IVA, or null when annual is not offered
   * to them. The 80 UI credit accrues monthly, so benefit tenants are billed
   * monthly only — a null here means the annual toggle must be hidden.
   */
  tenantPriceAnnual: number | null;
  /** Monthly 80 UI discount applied, sin IVA. 0 when no benefit applies. */
  fiscalBenefitDiscount: number;
  /**
   * Monthly partner-redemption discount applied, sin IVA. 0/undefined when
   * this tenant has no active partner redemption. Absent entirely on the
   * accountant-facing `getTenantPlansForTenant` variant — a partner code
   * discounts the redeeming tenant's OWN subscription, never a client an
   * accountant is looking at.
   */
  partnerDiscount?: number;
}

export interface TenantApiKeyRow {
  id: string;
  environment: "test" | "live";
  name: string | null;
  keyPrefix: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  /** The marca every invoice created with this key is issued under. */
  brandId: string | null;
}

export interface ApiIntegrationUsageResponse {
  calendarYear: number;
  calendarMonth: number;
  totalEmitted: number;
  emittedViaApi: number;
  maxCfesPerMonth: number | null;
}

export function getTenantCurrent(): Promise<TenantCurrentResponse> {
  return apiClient
    .get<TenantCurrentResponse>("/tenants/current")
    .then((res) => res.data);
}

/** Toggle whether Fiscalo auto-accepts received CFEs at day 19 of the
 *  20-día DGI decision window (§XVIII). */
export function updateTenantAutoAcceptReceivedCfe(
  autoAcceptReceivedCfe: boolean,
): Promise<{ autoAcceptReceivedCfe: boolean }> {
  return apiClient
    .patch<{ autoAcceptReceivedCfe: boolean }>(
      "/tenants/current/auto-accept-received-cfe",
      { autoAcceptReceivedCfe },
    )
    .then((res) => res.data);
}

export function createDgiPortalUser(
  body: {
    regenerate?: boolean;
    /** Override the CI used for the DGI portal user (e.g. the client's
     *  titular). When omitted the backend falls back to the acting user's
     *  own document. */
    documentNumber?: string;
    documentType?: "CI" | "NIE";
  } = {},
): Promise<{
  password: string;
  documentNumber: string;
  documentType: "CI" | "NIE";
}> {
  return apiClient
    .post<{
      password: string;
      documentNumber: string;
      documentType: "CI" | "NIE";
    }>("/tenants/current/dgi-portal-user", body)
    .then((res) => res.data);
}

/**
 * Accountant-flow companion to createDgiPortalUser. The accountant
 * already created the eFactura user on DGI; we just store the
 * credentials so the IdEmisor/proveedor-software automations can log in.
 */
export function saveManualDgiPortalUser(params: {
  documentType: "CI" | "NIE";
  documentNumber: string;
  password: string;
}): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>("/tenants/current/dgi-portal-user/manual", {
      documentType: params.documentType,
      documentNumber: params.documentNumber.trim(),
      password: params.password,
    })
    .then((res) => res.data);
}

/**
 * For the "Generar usuario nuevo" flow: the accountant has taken the
 * generated credentials, created the user on DGI's portal manually, and
 * is telling us they're done. Flips dgiPortalUserConfirmedAt server-side
 * so the onboarding step shows as complete.
 */
export function confirmDgiPortalUser(): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>("/tenants/current/dgi-portal-user/confirm", {})
    .then((res) => res.data);
}

export function updateTenantSucursal(
  cdgDGISucur: number,
): Promise<{ cdgDGISucur: number }> {
  return apiClient
    .patch<{ cdgDGISucur: number }>("/tenants/current/sucursal", {
      cdgDGISucur,
    })
    .then((res) => res.data);
}

export interface CurrentTenantRucInfoResponse {
  valid: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
}

export function getCurrentTenantRucInfo(): Promise<CurrentTenantRucInfoResponse> {
  return apiClient
    .get<CurrentTenantRucInfoResponse>("/tenants/current/ruc-info")
    .then((res) => res.data);
}

export function uploadCurrentTenantImage(
  file: File,
  target: "site" | "invoice" | "both" = "site"
): Promise<{ profileImage: string; invoiceLogoImage: string }> {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("target", target);
  return apiClient
    .post<{ profileImage: string; invoiceLogoImage: string }>(
      "/tenants/current/upload-image",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    )
    .then((res) => res.data);
}

/** Invoice-logo crop, as fractions of the source image (0..1) plus the point
 *  size of the box it is fitted into on the PDF. */
export interface InvoiceLogoCropPayload {
  invoiceLogoCropX: number;
  invoiceLogoCropY: number;
  invoiceLogoCropW: number;
  invoiceLogoCropH: number;
  invoiceLogoSizePt: number;
}

export interface UpdateTenantBrandingResponse extends InvoiceLogoCropPayload {
  brandColor: string | null;
}

/**
 * Make the invoice reuse the site logo. The backend clears invoiceLogoImage,
 * and every consumer resolves `invoiceLogoImage ?? profileImage`, so null is
 * exactly "same as the site". Also resets the crop, which belonged to the
 * image being dropped.
 */
export function useSiteLogoForInvoice(
  brandColor: string | null
): Promise<UpdateTenantBrandingResponse> {
  return apiClient
    .patch<UpdateTenantBrandingResponse>("/tenants/current/branding", {
      brandColor,
      useSiteLogoForInvoice: true,
    })
    .then((res) => res.data);
}

/**
 * Crop is optional and independent of the colour: the backend falls back to the
 * STORED crop for any field omitted here, so saving a colour alone never
 * resets a crop the user already drew.
 */
export function updateTenantBranding(
  brandColor: string | null,
  crop?: InvoiceLogoCropPayload
): Promise<UpdateTenantBrandingResponse> {
  return apiClient
    .patch<UpdateTenantBrandingResponse>("/tenants/current/branding", {
      brandColor,
      ...(crop ?? {}),
    })
    .then((res) => res.data);
}

export interface UpdateTenantExoneratedRegimePayload {
  exoneratedRegimeEnabled?: boolean | null;
  exoneratedRegimeLegend?: string | null;
  exoneratedRegimeHeaderLegend?: string | null;
}

export function updateTenantExoneratedRegime(
  payload: UpdateTenantExoneratedRegimePayload
): Promise<{
  exoneratedRegimeEnabled: boolean | null;
  exoneratedRegimeLegend: string | null;
  exoneratedRegimeHeaderLegend: string | null;
}> {
  return apiClient
    .patch<{
      exoneratedRegimeEnabled: boolean | null;
      exoneratedRegimeLegend: string | null;
      exoneratedRegimeHeaderLegend: string | null;
    }>("/tenants/current/exonerated-regime", payload)
    .then((res) => res.data);
}

/**
 * The tenant's standing adenda — the text printed on every invoice unless a
 * marca overrides it or the invoice suppresses it.
 *
 * Saving this does NOT change invoices already issued: the composed adenda is
 * snapshotted onto the invoice at issuance, so edits apply from now on. Say so
 * in the UI rather than letting a user assume a correction is retroactive.
 */
export function updateTenantAdenda(
  adendaText: string | null
): Promise<{ adendaText: string | null }> {
  return apiClient
    .patch<{ adendaText: string | null }>("/tenants/current/adenda", {
      adendaText,
    })
    .then((res) => res.data);
}

export function requestTenantDeletion(): Promise<void> {
  return apiClient
    .post("/tenants/current/request-deletion")
    .then(() => undefined);
}

export function sendOnboardingSupportRequest(
  type: "certificate_request" | "migration_help",
  options?: { notarialHandledByFiscalo?: boolean }
): Promise<void> {
  return apiClient
    .post("/tenants/current/onboarding-support-request", {
      type,
      ...(options?.notarialHandledByFiscalo === true && {
        notarialHandledByFiscalo: true,
      }),
    })
    .then(() => undefined);
}

export function sendFeedback(message: string): Promise<void> {
  return apiClient
    .post("/tenants/current/feedback", { message })
    .then(() => undefined);
}

export interface SupportMessageItem {
  id: string;
  direction: "support" | "tenant";
  body: string;
  authorUserId: string | null;
  authorDisplayName: string | null;
  createdAt: string;
  emailSentAt: string | null;
}

export function getSupportMessages(): Promise<SupportMessageItem[]> {
  return apiClient
    .get<SupportMessageItem[]>("/tenants/current/support-messages")
    .then((res) => res.data);
}

export function createSupportReply(
  message: string
): Promise<{ id: string; direction: string; body: string; createdAt: string }> {
  return apiClient
    .post<{ id: string; direction: string; body: string; createdAt: string }>(
      "/tenants/current/support-replies",
      { message: message.trim() }
    )
    .then((res) => res.data);
}

export function getTenantPlans(): Promise<TenantPlanOption[]> {
  return apiClient
    .get<TenantPlanOption[]>("/tenants/current/plans")
    .then((res) => res.data);
}

export interface TenantPlansForTenant {
  /** Tenant-priced catalog (80 UI discount applied from THIS client's
   *  declaration) — not the accountant's own context. */
  plans: TenantPlanOption[];
  /** Plan ids this client actually qualifies for; the picker filters to these. */
  eligiblePlanIds: string[];
}

/**
 * Client-aware plan catalog for the accountant generate-link picker: the given
 * tenant's eligible plans at the price that tenant will actually pay. Membership
 * gated server-side.
 */
export function getTenantPlansForTenant(
  tenantId: string,
): Promise<TenantPlansForTenant> {
  return apiClient
    .get<TenantPlansForTenant>(`/tenants/${tenantId}/plans`)
    .then((res) => res.data);
}

export interface PartnerBenefitDiscountStage {
  discountType: "flat" | "percent";
  /** Whole percentage points when `discountType` is "percent"; UYU cents when "flat". */
  discountValue: number;
  durationMonths: number;
}

/**
 * "Why is my plan discounted" for the plan-picker UI — whose benefit it is
 * and where the tenant stands in its stage timeline, alongside the plain
 * `partnerDiscount` amount already on `TenantPlanOption`. Null when there's
 * no redemption, or once every stage is exhausted (nothing left to explain).
 */
export interface PartnerBenefitStatus {
  partnerName: string;
  stages: PartnerBenefitDiscountStage[];
  monthsElapsed: number;
  /** Index into `stages`. Never null here — the backend already returns null
   *  for the whole response once every stage is exhausted. */
  currentStageIndex: number;
}

export function getCurrentPartnerBenefit(): Promise<PartnerBenefitStatus | null> {
  return apiClient
    .get<PartnerBenefitStatus | null>("/tenants/current/partner-benefit")
    .then((res) => res.data);
}

export function listTenantApiKeys(): Promise<TenantApiKeyRow[]> {
  return apiClient
    .get<TenantApiKeyRow[]>("/tenants/current/api-keys")
    .then((res) => res.data);
}

export function createTenantApiKey(body: {
  environment: "test" | "live";
  name?: string;
  brandId?: string | null;
}): Promise<{ id: string; keyPrefix: string; secret: string }> {
  return apiClient
    .post<{ id: string; keyPrefix: string; secret: string }>(
      "/tenants/current/api-keys",
      body
    )
    .then((res) => res.data);
}

/** Change a key's marca without re-issuing it — the secret is shown once, so
 *  swapping brands must not force a redeploy of whatever holds the key. */
export function setTenantApiKeyBrand(
  id: string,
  brandId: string | null
): Promise<{ brandId: string | null }> {
  return apiClient
    .patch<{ brandId: string | null }>(
      `/tenants/current/api-keys/${id}/brand`,
      { brandId }
    )
    .then((res) => res.data);
}

export function revokeTenantApiKey(id: string): Promise<void> {
  return apiClient.delete(`/tenants/current/api-keys/${id}`).then(() => undefined);
}

export type InvoiceWebhookSettings = {
  url: string | null;
  secretConfigured: boolean;
};

export function getInvoiceWebhookSettings(): Promise<InvoiceWebhookSettings> {
  return apiClient
    .get<InvoiceWebhookSettings>("/tenants/current/invoice-webhook")
    .then((res) => res.data);
}

export function patchInvoiceWebhookSettings(body: {
  url?: string | null;
  secret?: string | null;
}): Promise<InvoiceWebhookSettings> {
  return apiClient
    .patch<InvoiceWebhookSettings>("/tenants/current/invoice-webhook", body)
    .then((res) => res.data);
}

export function testInvoiceWebhook(): Promise<{ httpStatus: number; ok: boolean }> {
  return apiClient
    .post<{ httpStatus: number; ok: boolean }>("/tenants/current/invoice-webhook/test")
    .then((res) => res.data);
}

export function getApiIntegrationUsage(): Promise<ApiIntegrationUsageResponse> {
  return apiClient
    .get<ApiIntegrationUsageResponse>("/tenants/current/api-integration/usage")
    .then((res) => res.data);
}

export function updateTenantPlan(
  planId: string,
  billingInterval: "monthly" | "annual",
  /**
   * Where to land after a partner-covered remainder's dLocal payment page.
   * Only matters when the response carries a `redirectUrl` — otherwise
   * unused. Omit for callers with no sensible "back into the app" landing
   * spot; the backend falls back to a generic thank-you page.
   */
  returnContext?: "onboarding" | "settings"
): Promise<{ planId: string; billingInterval: string; redirectUrl?: string | null }> {
  return apiClient
    .patch<{ planId: string; billingInterval: string; redirectUrl?: string | null }>(
      "/tenants/current/plan",
      { planId, billingInterval, ...(returnContext ? { returnContext } : {}) }
    )
    .then((res) => res.data);
}

export function skipFiscalProfile(): Promise<void> {
  return apiClient
    .post("/tenants/current/skip-fiscal-profile")
    .then(() => undefined);
}

export function skipDgiCertificate(): Promise<void> {
  return apiClient
    .post("/tenants/current/skip-dgi-certificate")
    .then(() => undefined);
}

export function completeOnboarding(): Promise<void> {
  return apiClient
    .post("/tenants/current/complete-onboarding")
    .then(() => undefined);
}

/**
 * Promote the active tenant's DGI environment from test to prod. Server-side
 * checks all 5 auto-flow steps + certificate; returns the resulting env and
 * whether this call performed the flip.
 */
export function finalizeDgiProd(): Promise<{
  promoted: boolean;
  dgiEnv: "test" | "prod";
  reason?: "already_prod" | "auto_flow_incomplete" | "cert_missing";
}> {
  return apiClient
    .post<{
      promoted: boolean;
      dgiEnv: "test" | "prod";
      reason?: "already_prod" | "auto_flow_incomplete" | "cert_missing";
    }>("/tenants/current/finalize-dgi-prod")
    .then((res) => res.data);
}

/** Notifies tenant owner/admin that the accountant finished their onboarding steps. Call when accountant clicks "Ir al panel". */
export function notifyOwnerAccountantCompleted(): Promise<void> {
  return apiClient
    .post("/tenants/current/notify-owner-accountant-completed")
    .then(() => undefined);
}

export interface CurrentTenantDgiCredentialsStatus {
  hasCredentials: boolean;
  certificateValidFrom?: string;
  certificateValidTo?: string;
  certificateSubject?: string;
  certificateIssuer?: string;
  idEmisor?: number;
  configurationError?: boolean;
  lastConfigurationError?: string | null;
}

export function getCurrentTenantDgiCredentialsStatus(): Promise<CurrentTenantDgiCredentialsStatus> {
  return apiClient
    .get<CurrentTenantDgiCredentialsStatus>("/tenants/current/dgi-credentials/status")
    .then((res) => res.data);
}

export function downloadCurrentTenantDgiCertificate(): Promise<{
  blob: Blob;
  filename: string;
}> {
  return apiClient
    .get("/tenants/current/dgi-credentials/certificate", {
      responseType: "blob",
    })
    .then((res) => {
      const disposition = res.headers["content-disposition"] ?? "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      return {
        blob: res.data as Blob,
        filename: match?.[1] ?? "certificado.p12",
      };
    });
}

export function revealCurrentTenantDgiPassphrase(): Promise<{
  passphrase: string | null;
}> {
  return apiClient
    .get<{ passphrase: string | null }>(
      "/tenants/current/dgi-credentials/passphrase"
    )
    .then((res) => res.data);
}

/** File input accept value for signing-certificate uploads (PFX only). */
export const DGI_CERTIFICATE_FILE_ACCEPT = ".pfx";

export function uploadCurrentTenantDgiCredentials(
  file: File,
  idEmisor: number,
  passphrase?: string
): Promise<void> {
  const formData = new FormData();
  formData.append("p12", file);
  formData.append("idEmisor", String(idEmisor));
  if (passphrase != null && passphrase !== "") {
    formData.append("passphrase", passphrase);
  }
  return apiClient
    .post("/tenants/current/dgi-credentials", formData)
    .then(() => undefined);
}

export interface TenantDocument {
  id: string;
  name: string;
  fileUrl: string;
  contentType: string;
  originalFilename: string;
  createdAt: string;
}

export function getTenantDocuments(): Promise<TenantDocument[]> {
  return apiClient
    .get<TenantDocument[]>("/tenants/current/documents")
    .then((res) => res.data);
}

export function uploadTenantDocument(
  file: File,
  name?: string
): Promise<TenantDocument> {
  const formData = new FormData();
  formData.append("file", file);
  if (name != null && name.trim() !== "") {
    formData.append("name", name.trim());
  }
  return apiClient
    .post<TenantDocument>("/tenants/current/documents", formData)
    .then((res) => res.data);
}

/**
 * Upload the Literal E constancia (DGI form 6361 / 6906).
 *
 * A separate route from uploadTenantDocument on purpose: /tenants/current/documents
 * is premium-gated and literalE / semilla / tala do not carry premiumFeatures,
 * so the generic upload 403s for exactly the tenants this file belongs to. The
 * server names the document itself and keeps one per tenant.
 */
export function uploadLiteralEProof(file: File): Promise<TenantDocument> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient
    .post<TenantDocument>("/tenants/current/literal-e-proof", formData)
    .then((res) => res.data);
}

export function updateTenantDocumentName(
  id: string,
  name: string
): Promise<TenantDocument> {
  return apiClient
    .patch<TenantDocument>(`/tenants/current/documents/${id}`, { name: name.trim() })
    .then((res) => res.data);
}

export function deleteTenantDocument(id: string): Promise<void> {
  return apiClient
    .delete(`/tenants/current/documents/${id}`)
    .then(() => undefined);
}

export interface UpdateTenantParams {
  /** Public / commercial name (shown on invoices). */
  name?: string | null;
  legalName?: string | null;
  address?: string | null;
  city?: string | null;
  department?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
  activityStartDate?: string | null;
  literalE?: boolean | null;
  language?: string;
  industry?: string | null;
  profession?: string | null;
  migratingFromAnotherPlatform?: boolean | null;
  onboardingSituationChoice?:
    | "scratch"
    | "issuer_need_regularization"
    | "already_issuer"
    | null;
  onboardingMode?: "self" | "accountant" | null;
  /** ISO timestamp when user acknowledged step 6 "go to Abitab" (Fiscalo path). */
  step6AbitabAcknowledgedAt?: string | null;
  /** Self-service onboarding: CFE import step completed or skipped; null clears when navigating back to CAE. */
  onboardingCfeImportStepCompletedAt?: string | null;
  onboardingCertChoice?: "myself" | "fiscalo" | null;
  onboardingCertPath?: "manual" | "fiscalo" | null;
  onboardingCertAcquisitionDoneAt?: string | null;
  onboardingCertWaitContinuedAt?: string | null;
  onboardingPersonalizeDoneAt?: string | null;
}

export interface UpdateTenantResponse {
  tenantId: string;
  tenantName: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
}

export function updateTenant(
  params: UpdateTenantParams
): Promise<UpdateTenantResponse> {
  return apiClient
    .patch<UpdateTenantResponse>("/auth/tenant", params)
    .then((res) => res.data);
}

/**
 * Onboarding "ya facturé antes" auto-detect: asks the backend to classify the
 * acting tenant's RUT against DGI's emisor padrón into already_issuer (active
 * emisor, reuse cert) vs issuer_need_regularization (invoiced before, not
 * currently active → fresh cert). The backend fails soft to already_issuer, so
 * this only rejects on transport errors — callers should also fall back to
 * already_issuer if it throws.
 */
export function detectEmisorStatus(): Promise<
  "already_issuer" | "issuer_need_regularization"
> {
  return apiClient
    .get<{ status: "already_issuer" | "issuer_need_regularization" }>(
      "/auth/onboarding/emisor-status"
    )
    .then((res) => res.data.status);
}

export interface ImpersonateResponse {
  accessToken: string;
  sessionId: string;
  expiresAt: string;
}

export function impersonate(tenantId: string): Promise<ImpersonateResponse> {
  return apiClient
    .post<ImpersonateResponse>("/auth/impersonate", { tenantId })
    .then((res) => res.data);
}

export function endImpersonation(): Promise<ImpersonateResponse> {
  return apiClient
    .post<ImpersonateResponse>("/auth/end-impersonation")
    .then((res) => res.data);
}

export function applyVerifyResponse(response: VerifyResponse): void {
  setToken(response.accessToken);
  setSessionExpiresAt(response.expiresAt);
  setStoredUser({
    id: response.user.id,
    email: response.user.email,
    displayName: response.user.displayName ?? null,
    firstName: response.user.firstName ?? null,
    lastName: response.user.lastName ?? null,
    profileImage: response.user.profileImage ?? null,
  });
}

export function applyImpersonateResponse(response: ImpersonateResponse): void {
  setToken(response.accessToken);
  setSessionExpiresAt(response.expiresAt);
}

export function hasStoredToken(): boolean {
  return getToken() != null;
}

export interface TenantInvitationInfo {
  email: string;
  role: string;
  tenantId: string;
  tenantName: string;
  /** Tenant preferred language (e.g. 'es', 'en') for accept-invitation page. */
  language?: string;
  /** Tenant profile image URL for branding; when set, show in accept-invitation header. */
  tenantProfileImageUrl?: string | null;
  /** Tenant legal name; when no profile image, show in accept-invitation header. */
  tenantLegalName?: string;
  /** True when the invited email already has an account; use accept-existing flow. */
  userAlreadyExists?: boolean;
}

export function getTenantInvitationByToken(token: string): Promise<TenantInvitationInfo> {
  return apiClient
    .get<TenantInvitationInfo>(`/auth/invitations/${encodeURIComponent(token)}`)
    .then((res) => res.data);
}

export interface AcceptTenantInvitationParams {
  firstName: string;
  lastName: string;
  password: string;
}

export function acceptTenantInvitation(
  token: string,
  params: AcceptTenantInvitationParams
): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
      firstName: params.firstName.trim(),
      lastName: params.lastName.trim(),
      password: params.password,
    })
    .then((res) => res.data);
}

export function acceptTenantInvitationExistingUser(
  token: string
): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>(
      `/auth/invitations/${encodeURIComponent(token)}/accept-existing`
    )
    .then((res) => res.data);
}

export interface UpdateProfileParams {
  firstName?: string;
  lastName?: string;
}

export function updateProfile(params: UpdateProfileParams): Promise<{
  firstName: string | null;
  lastName: string | null;
}> {
  return apiClient
    .patch<{ firstName: string | null; lastName: string | null }>(
      "/auth/me",
      params
    )
    .then((res) => res.data);
}

export interface UpdateLegalDataParams {
  firstName: string;
  secondName?: string | null;
  firstLastName: string;
  secondLastName?: string | null;
  documentType: "national_id" | "passport";
  documentNumber: string;
}

export function updateLegalData(
  params: UpdateLegalDataParams
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>("/auth/me/legal-data", {
      firstName: params.firstName.trim(),
      secondName: params.secondName ?? null,
      firstLastName: params.firstLastName.trim(),
      secondLastName: params.secondLastName ?? null,
      documentType: params.documentType,
      documentNumber: params.documentNumber.trim(),
    })
    .then((res) => res.data);
}

export interface DgiPersonNameResponse {
  primerNombre: string | null;
  segundoNombre: string | null;
  primerApellido: string | null;
  segundoApellido: string | null;
}

export function getDgiPersonName(ci: string): Promise<DgiPersonNameResponse> {
  const trimmed = ci.replace(/\D/g, "").trim();
  return apiClient
    .get<DgiPersonNameResponse>("/auth/dgi/person-name", {
      params: { ci: trimmed },
    })
    .then((res) => res.data);
}

export function uploadProfileImage(file: File): Promise<{ profileImage: string }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiClient
    .post<{ profileImage: string }>("/auth/me/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function sendMyVerificationEmail(locale?: string): Promise<void> {
  return apiClient
    .post("/auth/me/send-verification-email", locale != null ? { locale } : {})
    .then(() => undefined);
}

export function verifyMyEmail(code: string): Promise<void> {
  return apiClient
    .post("/auth/me/verify-email", { code: code.trim() })
    .then(() => undefined);
}
